/**
 * デリバリーメトリクス表示
 *
 * 話し方（応答速度、フィラー率、声の安定性など）の詳細を表示
 */

import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Speed as SpeedIcon,
  RecordVoiceOver as VoiceIcon,
  GraphicEq as GraphicEqIcon,
  Pause as PauseIcon,
  Replay as ReplayIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon
} from '@mui/icons-material';

/**
 * スコアに応じたアイコンを取得
 */
const getScoreIcon = (score) => {
  if (score >= 80) return <CheckCircleIcon color="success" />;
  if (score >= 60) return <WarningIcon color="warning" />;
  return <ErrorIcon color="error" />;
};

/**
 * メトリクスカード
 */
function MetricCard({ icon, title, score, reasoning, additionalInfo }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        {icon}
        <Typography variant="subtitle2" sx={{ ml: 1, flex: 1 }}>
          {title}
        </Typography>
        <Chip
          label={`${score}点`}
          size="small"
          color={score >= 80 ? 'success' : score >= 60 ? 'warning' : 'error'}
        />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {reasoning}
      </Typography>
      {additionalInfo && (
        <Box sx={{ mt: 1 }}>
          {Object.entries(additionalInfo).map(([key, value]) => (
            <Typography key={key} variant="caption" display="block" color="text.disabled">
              {key}: {typeof value === 'number' ? value.toFixed(2) : value}
            </Typography>
          ))}
        </Box>
      )}
    </Paper>
  );
}

/**
 * デリバリーメトリクス表示コンポーネント
 */
export default function DeliveryMetrics({ deliveryScores, compositeScore, acousticAnalysis }) {
  if (!deliveryScores) {
    return (
      <Typography color="text.secondary">
        デリバリースコアのデータがありません
      </Typography>
    );
  }

  const metrics = [
    {
      icon: <SpeedIcon color="primary" />,
      title: '応答速度',
      data: deliveryScores.response_latency
    },
    {
      icon: <VoiceIcon color="primary" />,
      title: 'フィラー率',
      data: deliveryScores.filler_rate
    },
    {
      icon: <GraphicEqIcon color="primary" />,
      title: '声の安定性',
      data: deliveryScores.pitch_stability
    },
    {
      icon: <PauseIcon color="primary" />,
      title: 'ポーズ分布',
      data: deliveryScores.pause_distribution
    },
    {
      icon: <ReplayIcon color="primary" />,
      title: '言い直し率',
      data: deliveryScores.self_correction_rate
    }
  ];

  return (
    <Box>
      {/* 総合スコア */}
      <Box sx={{ mb: 3, textAlign: 'center' }}>
        <Typography variant="h5" fontWeight="bold" color="primary">
          デリバリースコア: {compositeScore}点
        </Typography>
        <Typography variant="body2" color="text.secondary">
          話し方の総合評価
        </Typography>
      </Box>

      {/* 各メトリクス */}
      <Grid container spacing={2}>
        {metrics.map((metric, index) => (
          <Grid item xs={12} sm={6} md={4} key={index}>
            <MetricCard
              icon={metric.icon}
              title={metric.title}
              score={metric.data?.score || 0}
              reasoning={metric.data?.reasoning || '評価データなし'}
              additionalInfo={metric.data?.additional_info}
            />
          </Grid>
        ))}
      </Grid>

      {/* 音響解析の詳細 */}
      {acousticAnalysis && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
            音響特徴の詳細
          </Typography>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Grid container spacing={2}>
              {/* ピッチ */}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>
                  ピッチ（声の高さ）
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="平均"
                      secondary={`${acousticAnalysis.candidate_metrics?.pitch?.mean?.toFixed(1) || '-'} Hz`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="変動（標準偏差）"
                      secondary={`${acousticAnalysis.candidate_metrics?.pitch?.std?.toFixed(1) || '-'} Hz`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="安定性スコア"
                      secondary={`${(acousticAnalysis.candidate_metrics?.pitch?.stability_score * 100)?.toFixed(0) || '-'}%`}
                    />
                  </ListItem>
                </List>
              </Grid>

              {/* 発話速度 */}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>
                  発話速度
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="単語数/分"
                      secondary={`${acousticAnalysis.candidate_metrics?.speaking_rate?.words_per_minute?.toFixed(0) || '-'} words/min`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="音節数/秒"
                      secondary={`${acousticAnalysis.candidate_metrics?.speaking_rate?.syllables_per_second?.toFixed(1) || '-'} syllables/sec`}
                    />
                  </ListItem>
                </List>
              </Grid>

              {/* ポーズ */}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>
                  ポーズ（間）
                </Typography>
                <List dense>
                  <ListItem>
                    <ListItemText
                      primary="ポーズ回数"
                      secondary={`${acousticAnalysis.candidate_metrics?.pauses?.total_count || '-'} 回`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemText
                      primary="平均ポーズ時間"
                      secondary={`${acousticAnalysis.candidate_metrics?.pauses?.avg_duration?.toFixed(2) || '-'} 秒`}
                    />
                  </ListItem>
                </List>
              </Grid>

              {/* 韻律同調 */}
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" gutterBottom>
                  韻律同調スコア
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Typography variant="h4" color="primary">
                    {(acousticAnalysis.prosody_sync_score * 100)?.toFixed(0) || '-'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    /100
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  面接官との話し方の同調度
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Box>
      )}
    </Box>
  );
}
